import type { Entitlements } from 'utils/types';
import useSession from 'hooks/useSession';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { createContainer } from 'unstated-next';
import env from 'utils/env';
import axios from 'axios';
import { Benefit } from 'pages/dashboard/course/blocks/PackageBenefit';
import { bookImage, bookMapping } from 'utils/helpers';

export type Book = {
  membership: string;
  thumbnail: string;
  name: string;
  base_price: number;
  discount_price: number;
  discount_percent: number;
  is_featured: boolean;
  meta: {
    lesson: number,
    unit: number,
    book?: number,
    level?: string,
    include?: string[]
  }
}

type CatalogVariant = {
  base_price: number;
  price: number;
  discount_percent: number;
  interface: {
    package: string;
    package_name: string;
    is_featured: boolean;
  };
};

type CatalogResponse = {
  code: string;
  name: string;
  photo_url: string;
  metadata: {
    lesson: number,
    unit: number,
    book?: number,
    level?: string,
  };
  benefits: Benefit[];
  variants: CatalogVariant[];
};

const useGlobalStore = () => {
  const { session, bootstrap, storeSession, signOut } = useSession();
  const [isNavOpened, setIsNavOpened] = useState<boolean>(false);
  const currentSessionLanguage = sessionStorage.getItem('i18nextLng') || 'vi';
  const [currentLanguage, setCurrentLanguage] = useState<string>(currentSessionLanguage);
  const [entitlements, setEntitlements] = useState<Entitlements>();
  const [bookData, setBookData] = useState<{[key: string]: Book[]}>({
    oxford: [],
    harper: [],
    ielts: [],
  });
  const navigate = useNavigate();
  
  const toggleNav = () => setIsNavOpened(!isNavOpened);
  
  const switchLanguage = (code: string) => {
    navigate('/');
    if (code === 'vi') {
      setCurrentLanguage('vi');
    } else if (code === 'en') {
      setCurrentLanguage('en');
    };
  };

  useEffect( () => {
    async function getUserTier() {
      if (session) {
        const config = env();
        const { data } = await axios.get(
          `${config.userServerApiUrl}/entitlement/api/v1/user/entitlements?encrypt=false`,
          {
            headers: {
              'x-session-token': session
            }
          }
        );
        if (data) {
          setEntitlements(data);
        }
      }
    }

    getUserTier();
  }, [session] );

  useEffect(()=> {
    async function fetchData() {
      const config = env();
      const { data } = await axios.get(
        `${config.baseApiUrl}/product-catalog/v1/site/tw-profile/paywall?country=TW`
      );
      if(data.catalogs) {
        const oxford : Book[]  = [];
        const harper : Book[]  = [];
        const ielts : Book[]  = [];
        const catalogs: CatalogResponse[] = data.catalogs;
        catalogs.forEach(catalog => {
          const code = catalog.code;
          const variant = catalog.variants;
          const isOxford = code.includes('oxford');
          const isHarper = code.includes('harcol') || code.includes('harpercollins');
          const isIelts = code.includes('ielts');
          const isBook = isOxford || isHarper || isIelts;

          if( !isBook ) return;
          const membership = bookMapping(variant[0].interface.package);
          const bookItem: Book = {
            membership,
            name: variant[0].interface.package_name,
            base_price: variant[0].base_price,
            discount_price: variant[0].price,
            discount_percent: variant[0].discount_percent,
            is_featured: variant[0].interface.is_featured,
            thumbnail: bookImage(membership),
            meta: catalog.metadata
          };
          if( isOxford ) {
            if(bookItem.is_featured) {
              oxford.unshift(bookItem);
            } else {
              oxford.push(bookItem)
            }
          } else if ( isHarper ) {
            if(bookItem.is_featured) {
              harper.unshift(bookItem);
            } else {
              harper.push(bookItem)
            }
          } else if ( isIelts ) {
            if(bookItem.is_featured) {
              ielts.unshift(bookItem);
            } else {
              ielts.push(bookItem)
            }
          }
        });
        // Update meta for Harper All Book and IELTS All Book
        if(harper.length) {
          harper[0].meta.include = []
          harper.forEach((item, index) => {
            if(index !== 0) {
              harper[0].meta.include?.push(item.name);
            }
          })
        }

        if(ielts.length) {
          ielts[0].meta.include = []
          ielts.forEach((item, index) => {
            if(index !== 0) {
              ielts[0].meta.include?.push(item.name);
            }
          })
        }

        setBookData({
          oxford,
          harper,
          ielts
        });
      }
    }
    // fetchData();
  }, [])

  return {
    session,
    bootstrap,
    storeSession,
    signOut,
    isNavOpened,
    toggleNav,
    currentLanguage,
    switchLanguage,
    entitlements,
    bookData
  };
};

const GlobalStore = createContainer(useGlobalStore);
export default GlobalStore;
